import { storeToState } from './taskManipulate';
import { isBlank } from 'utils/string';
import { IUser, IUsers } from '../users/usersType';
import {
  ICompany,
  IProjectMemberView,
  IProjectStore,
  IUserRoleView,
} from '../companies/companiesTypes';
import { IAssignmentView, ITask, IUserTaskRelView } from './tasksType';
import cloneDeep from 'lodash/cloneDeep';

function getAllUser(
  users: IUsers['map'],
  member: IProjectMemberView,
  company: ICompany
): IUser[] {
  if (member.userId) return [users[member.userId]];

  if (member.roleId) {
    return (
      company.roles[member.roleId].users.map(
        (it: IUserRoleView) => users[it.userId]
      ) || []
    );
  }
  return [];
}

export function buildDefaultFollowersFromProject(
  project: IProjectStore,
  users: IUsers['map'],
  company: ICompany,
  followerCanEdit: boolean
): { [userId: number]: IUserTaskRelView } {
  return Object.values([
    ...Object.values(project.projectMembers || {}),
    ...Object.values(project.projectRoleMembers || {}),
  ]).reduce((list: { [userId: number]: IUserTaskRelView }, item) => {
    if (item.implicit) return list;
    getAllUser(users, item, company).forEach((user) => {
      if (!user) return;
      list[user.id] = {
        userId: user.id,
        canEdit: followerCanEdit,
      };
    });
    return list;
  }, {});
}

export function createTaskFromTemplate(
  template: ITask,
  users: IUsers['map'],
  project: IProjectStore,
  company: ICompany,
  parent?: any
) {
  let customTemplate: any;
  if (parent) {
    // on subtask creation, parent's assignees should be add to template
    customTemplate = cloneDeep(template) || {};
    let assignee: { [userId: string]: any } = {};
    if (template?.useParentAssignees) {
      assignee = Object.values(parent.assignee).reduce(
        (list: { [userId: number]: any }, item: any) => {
          list[item.userId] = {
            name: users[item.userId!]?.name,
            progress: 0,
            userId: item.userId,
            properties: {
              roleName: item.properties?.roleName,
              roleId: item.properties?.roleId,
              implicit: item.properties?.implicit,
              title: item.properties?.title,
              projectRoleId: item.properties?.projectRoleId,
            },
          };
          return list;
        },
        {}
      ) as { [userId: number]: any };
    }
    customTemplate = {
      ...customTemplate,
      assignee,
    };
    if (template?.useParentVerifier) {
      customTemplate.verifiers = cloneDeep(
        Object.values(parent.verifiers) as IAssignmentView[]
      )
        .map((it: IAssignmentView) => {
          it.taskId = undefined;
          delete it.id;
          return it as IAssignmentView;
        })
        .reduce<{ [key: number]: IAssignmentView }>((current, item) => {
          const key =
            item.userId ||
            (item.properties?.roleId
              ? -item.properties.roleId
              : item.properties?.projectRoleId
                ? -item.properties.projectRoleId
                : undefined);
          if (key) current[key] = item;
          return current;
        }, {});
    }
  } else {
    customTemplate = storeToState(cloneDeep(template), users);
  }
  if (customTemplate['title'])
    customTemplate['title'] = customTemplate['title'].replace(
      '%PT',
      parent ? parent.title : ''
    );
  const projectTemplate =
    project?.templates &&
    project?.templates?.find((item) => item.taskId === template?.id);
  const taskPublisherMemberId =
    projectTemplate?.overrides?.taskPublisherMemberId ||
    customTemplate.taskPublisher?.userId ||
    -customTemplate.taskPublisher?.properties?.roleId ||
    -customTemplate.taskPublisher?.properties?.projectRoleId;
  const subTaskPublisherMemberId =
    projectTemplate?.overrides?.subTaskPublisherMemberId ||
    customTemplate.subTaskPublisher?.userId ||
    -customTemplate.subTaskPublisher?.properties?.roleId ||
    -customTemplate.subTaskPublisher?.properties?.projectRoleId;

  if (!customTemplate.verifiers) customTemplate.verifiers = {};
  const templateVerifiers: { [key: number]: IAssignmentView } = cloneDeep(
    customTemplate.verifiers
  );
  let verifierMembers: { [userId: number]: IAssignmentView } = {};

  [
    ...Object.values(project?.projectMembers || {}),
    ...Object.values(project?.projectRoleMembers || {})!,
  ].forEach((member: any) => {
    function fillRelatedPersonIfTitleMatch(title: string, firstUser: IUser) {
      if (title) {
        if (firstUser) {
          (
            customTemplate.fields?.find((it: any) => it.title === title) || {}
          ).value = firstUser?.name;
          if (title === customTemplate.followersTitle) {
            customTemplate.followers[member.userId] = {
              name: firstUser.name,
              userId: firstUser.id,
            };
          }
          // if (
          //   title === customTemplate.verifierTitle &&
          //   !verifierMembers[firstUser.id]
          // ) {
          //   verifierMembers[firstUser.id] = {
          //     name: firstUser.name,
          //     progress: 0,
          //     userId: firstUser.id,
          //     status: 'notStarted',
          //     type: 'verify',
          //   };
          // }

          if (
            title === customTemplate.taskPublisherTitle &&
            !customTemplate.taskPublisher?.userId
          ) {
            customTemplate.taskPublisher = {
              name: firstUser.name,
              progress: 0,
              userId: firstUser.id,
            };
          }
          if (
            title === customTemplate.subTaskPublisherTitle &&
            !customTemplate.subTaskPublisher
              ?.userId /*title match not overwrites existing person*/
          ) {
            customTemplate.subTaskPublisher = {
              name: firstUser.name,
              progress: 0,
              userId: firstUser.id,
            };
          }
        }
      }
    }
    const firstUser = getAllUser(users, member, company)[0];
    if (!firstUser) return;
    const memberProjectRoleTitle = company.roles[member.projectRoleId]?.name;
    fillRelatedPersonIfTitleMatch(memberProjectRoleTitle, firstUser);

    const memberRoleTitle = company.roles[member.relatedRoleId]?.name;
    fillRelatedPersonIfTitleMatch(memberRoleTitle, firstUser);

    verifierMembers = {
      ...verifierMembers,
      ...Object.values(templateVerifiers)
        .filter((item): item is IAssignmentView => !!item)
        .filter(
          (it) =>
            (member.projectRoleId &&
              member.projectRoleId === it?.properties?.projectRoleId) ||
            (member.relatedRoleId &&
              member.relatedRoleId === it?.properties?.roleId) ||
            (member.roleId && member.roleId === it?.properties?.roleId)
        )
        .reduce<{ [userId: number]: IAssignmentView }>((acc, it) => {
          const userId = it.userId || firstUser.id;
          acc[userId] = {
            ...it,
            name: users[userId]?.name || firstUser.name,
            userId,
          };
          return acc;
        }, {}),
    };

    if (
      member.projectRoleId === -taskPublisherMemberId ||
      member.relatedRoleId === -taskPublisherMemberId ||
      member.roleId === -taskPublisherMemberId
    ) {
      customTemplate.taskPublisher = {
        name: firstUser.name,
        progress: 0,
        userId: firstUser.id,
        properties: {
          roleId: -taskPublisherMemberId,
          roleName: company.roles[-taskPublisherMemberId].name,
        },
      };
    }
    if (
      member.projectRoleId === -subTaskPublisherMemberId ||
      member.roleId === -subTaskPublisherMemberId ||
      member.relatedRoleId === -subTaskPublisherMemberId
    ) {
      customTemplate.subTaskPublisher = {
        name: firstUser.name,
        progress: 0,
        userId: firstUser.id,
        properties: {
          roleId: -subTaskPublisherMemberId,
          roleName: company.roles[-subTaskPublisherMemberId].name,
        },
      };
    }
    if (customTemplate.assignee[-member.projectRoleId]) {
      delete customTemplate.assignee[-member.projectRoleId];
      customTemplate.assignee[firstUser.id] = {
        name: firstUser.name,
        progress: 0,
        userId: firstUser.id,
        properties: {
          projectRoleId: member.projectRoleId,
        },
      };
    }
    if (customTemplate.assignee[-member.relatedRoleId]) {
      delete customTemplate.assignee[member.relatedRoleId];
      customTemplate.assignee[firstUser.id] = {
        name: firstUser.name,
        progress: 0,
        userId: firstUser.id,
        properties: {
          relatedRoleId: member.relatedRoleId,
        },
      };
    }
    if (customTemplate.assignee[-member.roleId]) {
      delete customTemplate.assignee[member.roleId];
      customTemplate.assignee[firstUser.id] = {
        name: firstUser.name,
        progress: 0,
        userId: firstUser.id,
        properties: {
          relatedRoleId: member.roleId,
        },
      };
    }
  });

  if (!projectTemplate?.overrides?.verifierMemberId) {
    const userVerifiersFromTemplate = Object.values(templateVerifiers)
      .filter((it): it is IAssignmentView => !!it?.userId)
      .reduce<{ [userId: number]: IAssignmentView }>((acc, it) => {
        acc[it.userId] = users[it.userId]
          ? { name: users[it.userId].name, ...it }
          : it;
        return acc;
      }, {});

    customTemplate.verifiers = {
      ...userVerifiersFromTemplate,
      ...verifierMembers,
    };
  }

  if (projectTemplate?.overrides?.verifierMemberId) {
    if (!customTemplate.verifiers) customTemplate.verifiers = {};
    customTemplate.verifiers[projectTemplate.overrides.verifierMemberId] = {
      name: users[projectTemplate.overrides.verifierMemberId]?.name || '',
      progress: 0,
      userId: projectTemplate.overrides?.verifierMemberId || 0,
      properties: {
        roleId: projectTemplate.overrides?.verifierMemberId || 0,
        roleName:
          company.roles[projectTemplate.overrides?.verifierMemberId || 0]
            ?.name || '',
      },
    };
  }

  Object.values(customTemplate.assignee || {}).forEach((it: any) => {
    it.taskId = undefined;
  });
  if (projectTemplate && projectTemplate.overrides) {
    Object.entries(projectTemplate.overrides.fields).forEach(
      (value: [string, string]) => {
        const find = customTemplate.fields.find(
          (it: any) => it.name === value[0]
        );
        if (find && !isBlank(value[1])) find.value = value[1];
      }
    );
    Object.entries(projectTemplate.overrides.fieldOptions).forEach(
      (values: [string, string[]]) => {
        const find = customTemplate.fields.find(
          (it: any) => it.name === values[0]
        );
        if (!find) return;
        const options = find.options || {};
        values[1].forEach((value: string) => {
          options[value] = value;
        });
        find.options = options;
      }
    );

    if (taskPublisherMemberId > 0) {
      customTemplate.taskPublisher = {
        name: users[taskPublisherMemberId].name,
        progress: 0,
        userId: taskPublisherMemberId,
      };
    }
    if (subTaskPublisherMemberId > 0) {
      customTemplate.subTaskPublisher = {
        name: users[subTaskPublisherMemberId].name,
        progress: 0,
        userId: subTaskPublisherMemberId,
      };
    }
  }
  if (customTemplate.assigneeDefaultToProjectMembers) {
    customTemplate.assignee = Object.values([
      ...Object.values(project.projectMembers || {})!,
      ...Object.values(project.projectRoleMembers || {})!,
      //   TODO : resolve item any type
    ]).reduce((list: any, item: any) => {
      if (item.implicit) return list;
      const userList = getAllUser(users, item, company);
      userList.forEach((user) => {
        const role = company.roles[item?.roleId || item?.relatedRoleId];
        if (!list[user.id])
          list[user.id] = {
            name: users[user.id].name,
            progress: 0,
            userId: user.id,
            properties: {
              roleName: role?.name,
              roleId: role?.id,
              title: item.title,
              projectRoleId: item.projectRoleId,
              implicit: item.implicit,
            },
          };
        else if (list[user.id].properties) {
          list[user.id].properties.roleName =
            list[user.id].properties?.roleName || role?.name;
          list[user.id].properties.roleId =
            list[user.id].properties?.roleId || role?.id;
          list[user.id].properties.title =
            list[user.id].properties?.title || item.title;
          list[user.id].properties.projectRoleId =
            list[user.id].properties?.projectRoleId || item.projectRoleId;
          list[user.id].properties.implicit =
            list[user.id].properties?.implicit && item.implicit;
        }
      });
      return list;
    }, {});
  }
  if (customTemplate.followerDefaultToProjectMembers) {
    customTemplate.followers = buildDefaultFollowersFromProject(
      project,
      users,
      company,
      customTemplate.followerCanEdit
    );
  }
  customTemplate['projectId'] = project?.id;
  customTemplate['parentId'] = parent ? parent.id : undefined;
  customTemplate['templateId'] = customTemplate['id'];
  customTemplate['order'] = null;
  customTemplate['id'] = null;
  if (!customTemplate.taskPublisher?.userId)
    customTemplate.taskPublisher = null;
  if (customTemplate.taskPublisher?.userId) {
    customTemplate.taskPublisher!.taskId = null;
  }

  if (!customTemplate.subTaskPublisher?.userId)
    customTemplate.subTaskPublisher = null;
  if (customTemplate.subTaskPublisher?.userId) {
    customTemplate.subTaskPublisher!.taskId = null;
  }
  Object.entries(customTemplate.verifiers || {}).forEach(
    ([key, value]: [string, any]) => {
      if (!value?.userId || Number(key) <= 0) {
        delete customTemplate.verifiers[key];
      } else customTemplate.verifiers[key].taskId = null;
    }
  );
  Object.keys(customTemplate.assignee || {}).forEach((it) => {
    if (!it || Number(it) <= 0) {
      delete customTemplate.assignee[it];
    }
  });
  delete customTemplate['verifier'];
  return customTemplate;
}
